// ヒーローのAI（mch-dice の ai.js の移植）。プレイヤーと同じ情報（盤面・公開された行動順・自分の出目）だけを使い、
// 届くマス×行動の組を1手先まで点数化する。decide は「次の1コマンド」を返す（apply と同じ経路で実行する）。
import type { BuildingType, Command, ItemId, Side, TargetRef } from '../types';
import { BAD_STATUS_KEYS } from '../types';
import { CFG } from './config';
import { CELLS, adjacent, dist, neighbors } from './hex';
import type { Actor, Battle } from './state';
import * as R from './rules';
import { FREE_JUTSU } from '../data/jutsu';

const W = {
  core: 2.6,
  coreKill: 100000,
  hero: 2,
  heroKill: 260,
  neutral: 1,
  killValue: 0.9,
  bossKill: 220,
  paint: 3,
  paintEnemy: 4.5,
  progress: 6,
  potion: 1.3,
  /** 妖力：撃破ポイント1点の価値と、段階が上がる撃破の上乗せ。相手に取られそうな中立を先に取る価値（相手の分の割合） */
  surgePoint: 16,
  surgeStage: 70,
  surgeDeny: 0.5,
  /** 妖力の奥義を放つ代わりに失う妖力（段階3→1）の重さ */
  ougiCost: 110,
  /** 自本陣へ向かう攻城の妖：与ダメの重みと、倒したときの上乗せ */
  siegeHit: 1.5,
  siegeKill: 160,
  /** 攻城の妖が自本陣にこのマス数まで迫ったら迎え撃ちに行く */
  siegeGuardDist: 6,
  // ---- 新しい評価（v3）：式神の加勢・技の旗 ----
  /** 火傷（当てた相手に付く）の重み（次の手番の初めに燃えるぶん） */
  burn: 0.8,
  /** 倒すと加勢（鬼の怪力など）が出る手の上乗せ、術で加勢（狐火の囁き：術の待ちが明ける）が出る手の上乗せ */
  kaseiKill: 45,
  kaseiSkill: 24,
  /** 鳶口（eq_volley）：建物が一緒に撃つダメージの重み */
  volley: 0.8,
  /** 守りの普請（fushin_lead）：隣の自分の攻撃の建物1つあたり。的が射程に入っていればさらに */
  lead: 8,
  leadAim: 10,
  /** 3手番ごとの加勢（落雷・燎原の火・吹雪）の直前：周りの敵に当たるぶんの重み */
  blast: 0.5,
  /** 反撃（返し刀・あまのじゃく返し）で返すダメージの重み */
  counter: 0.6,
  /** 妖力が最大で本陣へ向かっているとき、中立を狩る値打ちの倍率（寄り道して長引かせない） */
  neutralLate: 0.6,
  /** 守りの普請（鬼の棟梁・版3）：本陣から2マス以内に攻撃の建物を建てる手の上乗せ（当たる回数ぶん。本陣の被ダメが下がる coreGuardMult。1.6 と 4 で 1.0倍は同じ、0.8倍の防衛で 4 が +3） */
  wall: 4,
};

/** 術の賽（rules 2）の威力の倍率の平均（合計の平均 10.5 → span の半分。ゾロ目の分も少し） */
function skillDiceMean(b: Battle, a: Actor): number {
  if ((b.setup.rules ?? 0) < 2) return 1;
  const k = CFG.dice3.skill;
  const base = 1 + 0.5 * k.span * (1 + (a.spec?.dice?.skill ?? 0));
  return base * (1 + (k.zorome - 1) / 36);
}
/**
 * 相手のヒーローが次の手番に side の本陣を叩けるか（自本陣の危険）。
 * v3：賽の最大（4）＋移動力＋自陣の分＋陣地の移動、術が明けていれば術の射程。旧：本陣まで4歩
 */
function foeThreatensCore(b: Battle, foe: Actor | undefined, side: Side, v3: boolean): boolean {
  if (!foe?.alive || foe.cell == null) return false;
  const d = walkDistance(b, foe, foe.cell, b.cores[side].cell);
  if (!v3) return d <= 4;
  const skill = (foe.turns ?? 0) + 1 >= (foe.skillReady ?? 0);
  const range = Math.max(R.attackRange(foe), skill ? R.skillRange(foe) : 0);
  const reach = Math.max(...CFG.dice) + R.heroMove(foe) + CFG.tilePaintBonus + R.tierValue(b, foe.side as Side, 'move');
  return d - range <= reach;
}
/**
 * 敵の武将（red）が新しい評価を使うルールの版。サーバーは戦闘ログを再生して検証するので、進行中の戦いの再現を変えないよう版で切り替える。
 * 2026-10-27：敵も新しい評価にすると（--rules 3）推奨戦力ちょうどのステージ勝率が章ボスで 5〜8 点落ちた（スズネおすすめ 90→82・攻城 95→89）ので、
 * RULES_VERSION は 2 のまま＝敵は据え置き。版を上げるときは build-sim --rules で必ず測り直す
 */
export const AI_V3_RULES = 3;
/** 新しい評価を使うか：自分（blue）はいつも、敵の武将は AI_V3_RULES から */
export const aiV3 = (b: Battle, a: Actor) => a.side === 'blue' || (b.setup.rules ?? 0) >= AI_V3_RULES;
/** 式神の加勢（条件 trigger）の残りがあればその定義 */
function kaseiLeft(a: Actor, trigger: string) {
  const o = a.spec?.ougi;
  return o && o.trigger === trigger && !a.ougiUsed ? o : null;
}
/** 回数に限りのあるお供の能力の残り回数 */
const compLeft = (a: Actor, k: string) => Math.max(0, R.compOf(a, k) - (a.compUsed?.[k] ?? 0));
/**
 * 倒されるかの目安のHP：踏みとどまり（comp:rise）と、HPが半分を切ったときの加勢の回復（清流の癒しなど。半分以上あるときだけ出る）を足す
 */
function deathHp(a: Actor): number {
  let hp = a.hp;
  if (compLeft(a, 'rise') > 0) hp += a.hpMax * R.compOf(a, 'rise');
  const pinch = kaseiLeft(a, 'pinch');
  if (pinch?.effects.heal && a.hp >= a.hpMax * CFG.flags.ougiPinch) hp += a.hpMax * pinch.effects.heal;
  return hp;
}

const TYPE_CAP: Record<BuildingType, number> = { yagura: 3, sakaku: 2, nobori: 2, otoshiana: 2, shuzen: 1, torii: 1, kitsunebi: 2, wara: 1, horoku: 2, ozutsu: 2 };
/** 攻撃の建物の値打ちの倍率（櫓＝1。大筒は2手番に1回、焙烙台は周りにも、藁人形は呪い・足止めのぶん） */
const SHOOTER_VALUE: Partial<Record<BuildingType, number>> = { yagura: 1, ozutsu: 0.5, horoku: 2, wara: 2.2 };

export interface AiOptions {
  /** 判断が粗いAI（人の代わりの検証用） */
  novice?: boolean;
  aggro?: number;
  /**
   * 方針。normal（既定）＝中立・龍脈・陣地・建築を評価する。
   * rush＝本陣直行（検証用）：中立・ガーディアンは狙わず、龍脈・陣地・宝箱・建築も評価しない。自本陣の防衛と回復（帰還・薬・祠）だけは残す
   */
  policy?: 'normal' | 'rush';
  /**
   * オートの目標（端末のAIの選択だけ。サーバーの検証＝replay には影響しない）。
   * shiki＝式神候補（エリート・章ボス）を自分で倒すまで本陣を落とさない。relays＝龍脈をすべて自陣にしてから本陣を落とす（★3）。
   * 手番の上限が近い・自本陣が危ないときは勝利を優先する
   */
  shiki?: boolean;
  relays?: boolean;
  /** 式神候補の enemyId（meta の SHIKIGAMI）。省略時はエリート・章ボスの段（tier elite/boss/raider） */
  shikiIds?: readonly string[];
  /** 立ち回りの癖（ビルドに合わせた操作。tools/sim/build-sim.ts）。省略した項目は 1（＝通常のAI） */
  style?: AiStyle;
  /** 新しい評価（式神の加勢・技の旗・役。aiV3）。decide が決める：自分はいつも、敵の武将は rules 3 から */
  v3?: boolean;
}

/**
 * 立ち回りの癖。重みはすべて倍率（既定 1）。
 * farm＝妖力が最大になるまで中立を優先して狩る。push＝HPがこの割合を超えていれば育ちを待たずに本陣へ（速攻）。
 * kite＝射程2以上から当てる手の加点（離れて削る）
 */
export interface AiStyle {
  core?: number;
  hero?: number;
  neutral?: number;
  paint?: number;
  build?: number;
  /** 建物の種類ごとの価値の倍率（build に掛けて使う） */
  buildType?: Partial<Record<BuildingType, number>>;
  /** 危険（次の手番までに受けそうなダメージ）の重み */
  risk?: number;
  kite?: number;
  farm?: boolean;
  /** farm で狩るのは妖力がこの段階になるまで（既定は最大） */
  farmUntil?: number;
  push?: number;
  /** 自分の建物の隣で手番を終える手の加点（建物を率いる・守る） */
  anchor?: number;
  /** 待機（構えて迎え撃つ）の加点 */
  wait?: number;
  /** 妖力が最大になったら敵本陣へ一直線に向かい、奥義を本陣に撃つ（検証用） */
  ougiRush?: boolean;
}
const styleOf = (opts: AiOptions): AiStyle => opts.style ?? {};

// ---------- オートの目標（式神候補・龍脈） ----------

const OBJ = {
  /** 手番の上限のこれだけ手前からは目標より勝利を優先 */
  turnMargin: 22,
  /** 砦（手番の上限が短い。rules 3）：上限のこれだけ手前からは勝利を優先 */
  castleMargin: 6,
  /** 砦：敵本陣を落とす見積もり（手番）×これ＋deadlinePad が残りの手番に届いたら、寄り道（中立・陣地・目標）をやめて本陣へ */
  deadlineSlack: 1,
  deadlinePad: 0,
  /** そのときの本陣への攻撃・本陣へ向かう移動の重み */
  deadlineCore: 2,
  deadlinePull: 2,
  /** 自本陣のHPがこの割合を切ったら勝利を優先 */
  coreLow: 0.35,
  /** 延長戦（本陣への攻撃が強まる）のこの段階からは勝利を優先 */
  overtime: 2,
  /** 本陣を落とせる場面：相手が自本陣を落とす見積もりが「目標を回って敵本陣を落とす」見積もりのこの倍以内なら落とす */
  raceSlack: 0.6,
  /** 目標を回っている間、相手のヒーローが自本陣からこの歩数以内なら迎え撃つ（攻撃の加点） */
  guardDist: 5,
  guardHit: 260,
  /** 目標を残したまま本陣を落とす手の減点 */
  holdKill: 2000,
  /** 目標の式神候補を倒す手の加点・削る手の倍率、龍脈を取る手の加点 */
  shikiKill: 420,
  shikiHit: 1.6,
  relayTake: 150,
  /** 殴る回数がこれを超える式神候補は（今の力では）目標にしない */
  maxHits: 7,
  /** 相手に横取りされそうな式神候補を先に狙う度合い（歩数） */
  steal: 3,
  /** 式神候補を龍脈より先に（歩数）。龍脈は敵本陣に近いものほど後に（歩数／マス） */
  shikiFirst: 1,
  relayOrder: 0.6,
  /** 目標へ向かう移動の重み（寄り道の小競り合いより優先） */
  pull: 2,
  /** 目標を回っている間、次の手番までに倒されそうなマスの減点（倒されると相手が本陣を攻め放題になる） */
  deathRisk: 700,
  /** 式神候補に挑む目安：HPの割合・倒しきったあとに残るHPの割合・エリートから受ける1手番の倍率（大技は予告を避けられる） */
  readyHp: 0.6,
  readyLeft: 0.2,
  eliteMult: 1.25,
  /** 射程2のヒーローが式神候補から受けるダメージの割合（離れて撃てる） */
  rangedTaken: 0.5,
};

interface Objectives {
  /** まだ倒していない式神候補（生きているもの） */
  shiki: Actor[];
  /** まだ自陣でない龍脈 */
  relays: number[];
  /** 目標が残っていて、本陣を落とすのを待つ */
  hold: boolean;
  /** 本陣を落とせる場面でも待つか（待つと相手に先に自本陣を落とされそうなら false） */
  holdKill: () => boolean;
}

const NO_OBJ: Objectives = { shiki: [], relays: [], hold: false, holdKill: () => false };

function isShikiActor(u: Actor, opts: AiOptions): boolean {
  if (u.kind === 'hero' || u.kind === 'building' || !u.enemyId) return false;
  if (opts.shikiIds) return opts.shikiIds.includes(u.enemyId);
  return u.tier === 'elite' || u.tier === 'boss' || u.tier === 'raider';
}

/** a が cell へ着くまでの手番の見積もり（賽の平均＋移動力で割る） */
function turnsTo(b: Battle, a: Actor, from: number, to: number, range = 0): number {
  const step = R.heroMove(a) + 2.5;
  return Math.max(0, walkDistance(b, a, from, to) - range) / step;
}

/** 本陣を落とすまでの手番の見積もり（届くまで＋殴る回数）。att が倒れていれば復活までを足す */
function coreRaceTurns(b: Battle, att: Actor, side: Side): number {
  const core = b.cores[side];
  const hit = Math.max(1, R.damageCalc(b, att, coreTarget(b, side)));
  const respawn = att.alive && att.cell != null ? 0 : Math.max(0, ((att.respawnAt ?? b.time) - b.time) / R.interval(b, att)) + 1;
  const from = att.cell ?? b.homes[att.side as Side];
  return respawn + turnsTo(b, att, from, core.cell, R.attackRange(att)) + Math.ceil(core.hp / hit);
}

/** 砦（手番の上限が短い）：残りの手番が、敵本陣を落とすまでの見積もりに迫っているか（時間切れで負けるのは自分＝青だけ） */
function deadlineNear(b: Battle, a: Actor | undefined): boolean {
  if (!a || a.side !== 'blue' || !R.castleTurnLimited(b)) return false;
  const left = R.turnLimitOf(b) - Math.max(1, b.stats.playerTurns) + 1;
  return left <= coreRaceTurns(b, a, 'red') * OBJ.deadlineSlack + OBJ.deadlinePad;
}

/** 勝利を優先すべきか：手番の上限が近い・延長戦・自本陣が危ない */
function objectiveUrgent(b: Battle, side: Side): boolean {
  // 砦（手番の上限が短い）は残り OBJ.castleMargin 手番か、本陣を落とす見積もりが残りに迫ったら勝利を優先（式神候補・龍脈の寄り道はそれまで）
  if (b.stats.playerTurns >= R.turnLimitOf(b) - (R.castleTurnLimited(b) ? OBJ.castleMargin : OBJ.turnMargin)) return true;
  if (deadlineNear(b, R.heroOf(b, side))) return true;
  // 延長戦（本陣への攻撃が強まる）に入ったら長引かせない
  if (R.overtimeLevel(b) >= OBJ.overtime) return true;
  const core = b.cores[side];
  if (core.hp < core.max * OBJ.coreLow) return true;
  return false;
}

/** 目標を回ってから敵本陣を落とす見積もりが、相手が自本陣を落とす見積もりに追いつかないか（本陣を落とせる場面で、待つかどうか） */
function raceLost(b: Battle, a: Actor, shiki: Actor[], relays: number[]): boolean {
  const side = a.side as Side;
  const foe = R.heroOf(b, R.enemyOf(side));
  if (!foe) return false;
  // 自分：近い目標から順に回り（式神候補は殴る回数も）、最後に敵本陣
  let at = a.cell ?? b.homes[side];
  let mine = 0;
  const left = [...relays.map((c) => ({ cell: c, hits: 0 })), ...shiki.map((u) => ({ cell: u.cell!, hits: Math.ceil(u.hp / Math.max(1, R.damageCalc(b, a, u))) }))];
  while (left.length) {
    let k = 0;
    for (let i = 1; i < left.length; i++) if (dist(at, left[i].cell) < dist(at, left[k].cell)) k = i;
    const t = left.splice(k, 1)[0];
    mine += turnsTo(b, a, at, t.cell, t.hits ? R.attackRange(a) : 0) + t.hits;
    at = t.cell;
  }
  const saved = a.cell;
  a.cell = at;
  try {
    mine += coreRaceTurns(b, a, R.enemyOf(side));
  } finally {
    a.cell = saved;
  }
  const theirs = coreRaceTurns(b, foe, side);
  return theirs <= mine * OBJ.raceSlack;
}

export function objectivesOf(b: Battle, side: Side, opts: AiOptions): Objectives {
  if ((!opts.shiki && !opts.relays) || b.defense || opts.policy === 'rush') return NO_OBJ;
  const shiki: Actor[] = [];
  if (opts.shiki) {
    // 同じ種類を一度でも自分が倒していれば済み（勧誘は種類ごと）。相手に倒されて残りが無ければ諦める
    // 今の力で倒しきれない相手（殴る回数が多すぎる）は待っても勝ち目を減らすだけなので目標から外す
    const a = R.heroOf(b, side);
    for (const u of b.actors)
      if (u.alive && u.cell != null && u.side !== side && isShikiActor(u, opts) && !(b.stats.kills[u.enemyId!] > 0))
        if (!a || feasible(b, a, u)) shiki.push(u);
  }
  const relays: number[] = [];
  if (opts.relays) b.tiles.forEach((t, i) => t.relay && t.owner !== side && relays.push(i));
  const pending = shiki.length + relays.length > 0;
  const a = R.heroOf(b, side);
  const hold = pending && !!a && !objectiveUrgent(b, side);
  let memo: boolean | null = null;
  return { shiki, relays, hold, holdKill: () => (memo ??= hold && !raceLost(b, a!, shiki, relays)) };
}

/**
 * 式神候補に挑める状態か：倒しきるまでの手番に受けるダメージ（相手の手番の速さ・射程2なら殴られにくい）を見積もり、
 * 薬の回復を足しても余裕が残るか。離れると全快に戻る相手なので、挑むなら倒しきる
 */
function readyFor(b: Battle, a: Actor, u: Actor): boolean {
  const hit = Math.max(1, R.damageCalc(b, a, u, R.skillReady(a)));
  const hits = Math.ceil(u.hp / hit);
  if (hits <= 1) return true;
  // 番人・章ボスの大技は避けにくい。エリートの大技は予告のマスを避けられる
  const strike = u.kind === 'boss' ? (1 + CFG.bossStrikeMult) / 2 : OBJ.eliteMult;
  const per = R.damageCalc(b, u, a, false, strike) * Math.min(2, R.interval(b, a) / R.interval(b, u)) * (R.attackRange(a) >= 2 ? OBJ.rangedTaken : 1);
  const heal = R.itemCount(a, 'heal') * (a.spec?.potionHeal ?? 0);
  return a.hp + heal - per * hits >= a.hpMax * OBJ.readyLeft && a.hp >= a.hpMax * OBJ.readyHp;
}

/** 今の力で倒しきれる見込みがあるか：殴る回数が多すぎず、HPが満ちていれば挑める。無理な相手を待っても勝ち目を減らすだけ */
function feasible(b: Battle, a: Actor, u: Actor): boolean {
  if (u.hp / Math.max(1, R.damageCalc(b, a, u)) > OBJ.maxHits) return false;
  const hp = a.hp;
  a.hp = a.hpMax;
  try {
    return readyFor(b, a, u);
  } finally {
    a.hp = hp;
  }
}

/** 目標の中で次に向かう先（近さと危なさ）。挑めない式神候補しか残っていなければ、回復（帰還マス）か null（通常の判断） */
function objectiveGoal(b: Battle, a: Actor, obj: Objectives): number | null {
  const side = a.side as Side;
  const foe = R.heroOf(b, R.enemyOf(side));
  let best: number | null = null;
  let bestS = Infinity;
  const foeCore = b.cores[R.enemyOf(side)].cell;
  for (const cell of obj.relays) {
    let s = walkDistance(b, a, a.cell!, cell);
    // 敵本陣に近い龍脈（取り返されやすい）は最後に回し、取ったらそのまま本陣を落としに行く
    s -= dist(cell, foeCore) * OBJ.relayOrder;
    // 相手のヒーローのそば（取ってもすぐ取り返される・挟まれる）は少し後回し
    if (foe?.alive && foe.cell != null && dist(foe.cell, cell) <= 2) s += 2;
    if (s < bestS) (bestS = s), (best = cell);
  }
  let unready = false;
  for (const u of obj.shiki) {
    if (!readyFor(b, a, u)) {
      unready = true;
      continue;
    }
    // 式神候補は相手に倒されると取り返せない（龍脈は取り返せる）ので先に
    let s = walkDistance(b, a, a.cell!, u.cell!) - OBJ.shikiFirst;
    // 相手のヒーローが削っている・近い：横取りされる前に（倒されると勧誘の機会が無くなる）
    if (foe?.alive && foe.cell != null && dist(foe.cell, u.cell!) <= 4) s -= u.hp <= R.damageCalc(b, foe, u, true) * 2 ? OBJ.steal : 0;
    if (s < bestS) (bestS = s), (best = u.cell!);
  }
  if (best != null) return best;
  // 挑めない式神候補だけが残っている：HPが減っていれば回復しに戻る。満ちていれば通常の判断で育つ（本陣は落とさない）
  if (unready && a.hp < a.hpMax * OBJ.readyHp && R.itemCount(a, 'heal') === 0) return R.returnPoint(b, a);
  return null;
}

/** 撃破ポイント points（stages は即上昇の段数）を side が得たときの価値。最大段階なら0 */
function surgeValue(b: Battle, side: Side, points: number, stages = 0): number {
  if (!CFG.surge.enabled) return 0;
  const s = R.surgeOf(b, side);
  const max = R.surgeMax();
  if (s.stage >= max) return 0;
  let after = R.surgeStageFor(s.points + points, R.surgeThresholds(b, side));
  if (stages > 0) after = Math.max(after, s.stage + stages);
  return points * W.surgePoint + (Math.min(max, after) - s.stage) * W.surgeStage;
}

/** 現在の手番のヒーローの次の1手 */
/** そのマスから相手の本陣を割れる攻撃（通常・術・奥義の順に探す）。sealed＝そこへ動くと封印がかかる（術・奥義は使えない） */
function lethalAt(b: Battle, a: Actor, cell: number, sealed = false): Command | null {
  const foe = R.enemyOf(a.side as Side);
  const saved = a.cell;
  a.cell = cell;
  try {
    const modes: [boolean, boolean][] = [[false, false]];
    if (!sealed && R.skillReady(a)) modes.push([true, false]);
    if (!sealed && !R.ougiBlock(b, a)) modes.push([true, true]);
    for (const [skill, ougi] of modes) {
      const refs = ougi ? R.castingOugi(a, () => R.attackTargetsAt(b, a, cell, true)) : R.attackTargetsAt(b, a, cell, skill);
      for (const ref of refs)
        if (ref.kind === 'core' && ref.side === foe && R.forecastFor(b, a, ref, skill, ougi)?.kill)
          return ougi ? { t: 'attack', target: ref, skill: true, ougi: true } : { t: 'attack', target: ref, skill: skill || undefined };
    }
    // 自分を中心に放つ術（縮地の奥義など）で本陣を割れるか：盤を写して確かめる
    const j = R.castOf(a);
    if (j && !sealed)
      for (const ougi of [false, true]) {
        if (ougi ? R.ougiBlock(b, a) : !R.skillReady(a)) continue;
        const p = R.castPower(a, ougi);
        if (!p) continue;
        const lands = j.kind === 'leap' ? R.leapCells(b, a, p.radius).filter((c) => adjacent(c, b.cores[foe].cell)) : [undefined];
        for (const to of lands) if (castKillsCore(b, a, cell, to, ougi)) return { t: 'cast', ...(to != null ? { cell: to } : {}), ...(ougi ? { ougi: true } : {}) };
      }
    return null;
  } finally {
    a.cell = saved;
  }
}

/**
 * リーサル：この行動で相手の本陣を割れるなら必ず割る（自分・敵とも、行動のたびに確かめる）。
 * 動く前ならその場から、賽を振った後なら届くマスへ動いてから。オートの目標で本陣を落とすのを待っているときは除く
 */
function lethal(b: Battle, a: Actor, opts: AiOptions): Command | null {
  if (b.defense || a.dashing) return null;
  const obj = objectivesOf(b, a.side as Side, opts);
  if (obj.hold && obj.holdKill()) return null;
  if (b.phase === 'start' || b.phase === 'moved') return lethalAt(b, a, a.cell as number);
  if (b.phase === 'rolled') {
    // 陣地ルールで相手の陣地に踏み込むと封印がかかる章：その道では術・奥義を数えない
    const seal = R.footing(b).afflict?.key === 'seal';
    const reach = R.reachMap(b);
    for (const [cell, r] of reach) {
      if (!r.stop) continue;
      const sealed = seal && R.paintRoute(b, a, cell).slice(1).some((c) => b.tiles[c].owner === R.enemyOf(a.side as Side));
      if (lethalAt(b, a, cell, sealed)) return { t: 'move', to: cell };
    }
  }
  return null;
}

export function decide(b: Battle, opts: AiOptions = {}): Command {
  const a = R.currentActor(b);
  if (!a || a.kind !== 'hero') return { t: 'wait' };
  opts = { ...opts, v3: aiV3(b, a) };
  const kill = lethal(b, a, opts);
  if (kill) return kill;
  const item = itemChoice(b, a, opts);
  if (item) return item;
  // 溜めた術：今いるマスから撃てるなら動く前に撃つ
  if (b.phase === 'start' && a.charged) {
    const plan = bestPlan(b, a, opts, [a.cell as number]);
    if (plan?.act === 'attack' && plan.skill && plan.target && !plan.ougi) return { t: 'attack', target: plan.target, skill: true };
  }
  if (b.phase === 'start') return { t: 'roll' };
  if (b.phase === 'rolled' && a.dashing) return { t: 'move', to: dashCell(b, a, opts) };
  if (b.phase === 'rolled') {
    const plan = bestPlan(b, a, opts, null);
    return { t: 'move', to: plan ? plan.cell : (a.cell as number) };
  }
  // 移動後：支援の術（手番を終えない）に価値があれば先に放つ
  const free = freeCast(b, a, opts);
  if (free) return free;
  // 移動後：価値のある建物があれば建て（行動を消費しない）、それから行動
  const ctx = buildContext(b, a);
  if (opts.policy !== 'rush' && (!opts.novice || (a.builtThisTurn ?? 0) === 0)) {
    const best = buildOptions(b, a, ctx, opts)[0];
    if (best && best.value > 0) return { t: 'build', type: best.type, cell: best.at };
  }
  const plan = bestPlan(b, a, opts, [a.cell as number]);
  if (!plan) return { t: 'wait' };
  if (plan.act === 'attack' && plan.target) return plan.ougi ? { t: 'attack', target: plan.target, skill: true, ougi: true } : { t: 'attack', target: plan.target, skill: plan.skill || undefined };
  if (plan.act === 'cast') return { t: 'cast', ...(plan.leap != null ? { cell: plan.leap } : {}), ...(plan.ougi ? { ougi: true } : {}) };
  if (plan.act === 'potion' && !R.itemBlock(b, a, 'heal')) return { t: 'item', id: 'heal' };
  return { t: 'wait' };
}

/** アイテム（手番を終えない・1手番に1回）：使い時なら返す */
function itemChoice(b: Battle, a: Actor, opts: AiOptions): Command | null {
  if (opts.novice && (a.turns ?? 0) % 2 === 1) return null; // 粗いAIは使い時を半分逃す
  const can = (id: ItemId) => !R.itemBlock(b, a, id);
  const side = a.side as Side;
  const max = a.hpMax;
  const danger = threatAt(b, a, a.cell!);
  const bad = BAD_STATUS_KEYS.some((k) => R.hasStatus(a, k));
  if (can('purify') && bad) return { t: 'item', id: 'purify' };
  if (b.phase === 'start') {
    // 撃破ポイントがあと少しで次の段階：先に上げる
    if (can('spirit')) {
      const s = R.surgeOf(b, side);
      const th = R.surgeThresholds(b, side)[s.stage];
      if (th != null && th - s.points <= (CFG.items.spirit.points ?? 2)) return { t: 'item', id: 'spirit' };
    }
    // 目的地が遠い：韋駄天
    if (can('swift') && walkDistance(b, a, a.cell!, goalCell(b, a, opts)) > 4 + b.bonus) return { t: 'item', id: 'swift' };
    return null;
  }
  if (b.phase !== 'moved') return null;
  // 回復薬は手番を終えないので、減ったら早めに（溢れる分が少なければ）飲む
  const ph = a.spec?.potionHeal ?? 0;
  if (can('heal') && (a.hp - danger < max * CFG.itemAiHeal.afterHit || (a.hp < max * CFG.itemAiHeal.below && max - a.hp >= ph * 0.7))) return { t: 'item', id: 'heal' };
  if (can('guard') && danger >= max * 0.3) return { t: 'item', id: 'guard' };
  if (can('power')) {
    // 強い相手（ヒーロー・ボス・本陣・防衛戦の攻め手）を殴る手番に
    const plan = bestPlan(b, a, opts, [a.cell as number]);
    const t = plan?.act === 'attack' && plan.target ? plan.target : null;
    const u = t && t.kind === 'actor' ? R.actorById(b, t.id) : null;
    if (t && (t.kind === 'core' || u?.kind === 'hero' || u?.kind === 'boss' || u?.attacker || u?.tier === 'elite')) return { t: 'item', id: 'power' };
  }
  return null;
}

/** 一撃離脱の移動先：危険の少ないマス（同じなら目的地に近い・塗れるマス） */
function dashCell(b: Battle, a: Actor, opts: AiOptions): number {
  const reach = R.reachMap(b);
  const home = a.cell as number;
  const goal = goalCell(b, a, opts);
  const k = 50 / Math.max(30, a.atk);
  let best = home;
  let bestScore = -Infinity;
  // 経路は動かす前の位置から（下で a.cell を仮に動かすため、先に求めておく）
  const routes = new Map([...reach].filter(([, r]) => r.stop).map(([c]) => [c, R.paintRoute(b, a, c)]));
  try {
    for (const [cell, r] of reach) {
      if (!r.stop) continue;
      a.cell = cell;
      let score = -threatAt(b, a, cell) * k - walkDistance(b, a, cell, goal) * W.progress * 0.5;
      if (opts.policy !== 'rush') for (const c of routes.get(cell)!.slice(1)) if (R.paintable(b, c) && b.tiles[c].owner !== a.side) score += W.paint;
      score += ((cell * 7) % 5) * 0.01;
      if (score > bestScore) {
        bestScore = score;
        best = cell;
      }
    }
  } finally {
    a.cell = home;
  }
  return best;
}

function power(b: Battle, att: Actor | undefined, def: Actor): number {
  if (!att || !att.alive) return 0;
  return R.damageCalc(b, att, def) * att.hp;
}

const coreTarget = (b: Battle, side: Side): R.CoreTarget => ({ kind: 'core', side, ...b.cores[side] });

/** 次の自分の手番までに受けそうなダメージ。相手の移動は公開された賽の分布で平均する。detail に殴ってきそうな相手と確率を集める */
export function threatAt(b: Battle, a: Actor, cell: number, ignoreId: number | null = null, detail?: Threat[]): number {
  const horizon = b.time + R.interval(b, a);
  let total = 0;
  for (const u of b.actors) {
    if (u.kind === 'building') {
      if (u.alive && u.side !== a.side && R.isShooter(u.type) && dist(u.cell!, cell) <= (u.range ?? 2)) total += (u.power ?? 0) * (SHOOTER_VALUE[u.type!] === 0.5 ? 0.5 : 1);
      continue;
    }
    if (!u.alive || u.cell == null || u.side === a.side || u.id === ignoreId || u.nextAt > horizon) continue;
    const repeats = Math.min(3, 1 + Math.floor((horizon - u.nextAt) / R.interval(b, u)));
    let damage = 0;
    let p = 1;
    // 構えた敵の武将（版3）：構えたマスへ術が来る（動かない）。外へ出れば空振りで、そのあと普通に動いて殴る（術の待ちは構えたときから）
    if (u.kind !== 'hero' && u.telegraph?.cells.includes(cell)) damage = R.damageCalc(b, u, a, false, u.kind === 'boss' ? CFG.bossStrikeMult : CFG.eliteStrikeMult);
    else if (u.kind === 'hero') {
      const distance = walkDistance(b, u, u.cell, cell);
      // 構えの要る敵の武将（版3）：術は構えた相手にだけ来る。構えていなければこの手番は通常攻撃まで（術は手番の終わりに構えてから）
      const skill = (u.turns ?? 0) + 1 >= (u.skillReady ?? 0) && (!R.rivalCharges(b, u) || !!u.telegraph?.wait.some((w) => w.id === a.id));
      const range = skill ? R.skillRange(u) : R.attackRange(u);
      const bonus = R.heroMove(u) + (b.tiles[u.cell].owner === u.side ? 1 : 0);
      const faces = CFG.dice.map((v) => Math.max(u.spec!.diceMin, v));
      p = faces.filter((face) => distance <= face + bonus + range).length / faces.length;
      // 勢い（momentum）：2マス以上動いてから殴ると +20%。相手は塗りながら回り込んで来るので見込む
      const momentum = R.hasFlag(u, 'momentum') && (u.moved ?? 0) < 2 ? CFG.flags.momentum : 1;
      damage = p * R.damageCalc(b, u, a, skill) * momentum * (skill && u.telegraph ? CFG.v3.rivalCharge.mult : 1);
    } else if (u.attacker) {
      if (dist(u.cell, cell) <= CFG.defense.steps + 1) damage = R.damageCalc(b, u, a);
    } else if (u.kind === 'boss') {
      if (adjacent(u.cell, cell)) damage = R.damageCalc(b, u, a);
    } else if (dist(cell, u.camp!) <= 2 && dist(u.cell, cell) <= 2 && (u.engaged || adjacent(u.cell, cell))) damage = R.damageCalc(b, u, a);
    total += damage * repeats;
    if (detail && damage > 0) detail.push({ u, p: Math.min(1, p * repeats), hit: p > 0 ? damage / p : damage, melee: !u.telegraph });
  }
  return total;
}

/** threatAt の内訳：殴ってきそうな相手、その確率、1発の大きさ、隣から殴る通常の攻撃か（大技の予告でなく） */
interface Threat {
  u: Actor;
  p: number;
  hit: number;
  melee: boolean;
}
/** 傘の守り（comp:kasa）・加勢の守り（ougiGuard）：最大HPの kasaMin 以上の大きな攻撃を回数まで防ぐ。防げる分を危険から引く */
function guardedDanger(a: Actor, raw: number, threats: Threat[]): number {
  const guards = compLeft(a, 'kasa') + (a.ougiGuard ?? 0);
  if (guards <= 0) return raw;
  const big = threats.filter((t) => t.hit >= a.hpMax * CFG.flags.kasaMin).sort((x, y) => y.hit - x.hit).slice(0, guards);
  return Math.max(0, raw - big.reduce((s, t) => s + t.hit * t.p, 0));
}
/** 隣から殴られたときに返す反撃の倍率（返し刀・あまのじゃく返し・反撃の銘。構えは castScore で別に） */
function counterMult(a: Actor): number {
  const flag = Math.max(R.hasFlag(a, 'bu_counter') || R.hasFlag(a, 'counter') ? CFG.flags.counter : 0, R.hasFlag(a, 'kaeshi') ? CFG.flags.kaeshi : 0);
  return Math.max(flag, R.compOf(a, 'mirror'));
}
/** そのマスで手番を終えたとき、隣から殴ってくる相手に返す反撃の見込み（threatAt の detail から。遠くから撃つ相手には返せない） */
function counterValue(b: Battle, a: Actor, threats: Threat[]): number {
  const mult = counterMult(a);
  if (mult <= 0) return 0;
  let v = 0;
  for (const { u, p, melee } of threats) if (melee && R.attackRange(u) <= 1 && u.kind !== 'building') v += p * Math.min(u.hp, R.damageCalc(b, a, u, false, mult));
  return v;
}

/** 地形込みの最短距離。目的地は埋まっていてもよい。相手の建物（見えない落とし穴を除く）は壊して通る想定で3歩分 */
export function walkDistance(b: Battle, a: Actor, from: number, to: number): number {
  if (from === to) return 0;
  const queue: [number, number][] = [[from, 0]];
  const seen = new Set([from]);
  // 重み付きなので簡易ダイクストラ（盤面は71マス）
  while (queue.length) {
    let bi = 0;
    for (let k = 1; k < queue.length; k++) if (queue[k][1] < queue[bi][1]) bi = k;
    const [cell, d] = queue.splice(bi, 1)[0];
    for (const n of neighbors(cell)) {
      if (n === to) return d + 1;
      if (seen.has(n)) continue;
      const wall = b.actors.find((u) => u.alive && u.kind === 'building' && u.type !== 'otoshiana' && u.cell === n && u.side !== a.side);
      if (!wall && !R.passable(b, n, a) && !R.throughable(b, n, a)) continue;
      seen.add(n);
      queue.push([n, d + (wall ? 3 : 1)]);
    }
  }
  return 99;
}

/** 防衛戦：攻め手がいれば自本陣に一番近いものへ。準備中は龍脈・野良（寝返る前に倒す）、何も無ければ本陣の前 */
function defenseGoal(b: Battle, a: Actor): number {
  const side = a.side as Side;
  const core = b.cores[side].cell;
  const guard = defenseGuardCell(b, side);
  // 波の最中：本陣に近い攻め手から。遠くの攻め手は追わず、本陣の前で待つ（櫓の射程で迎え撃つ）
  const attackers = b.actors.filter((u) => u.alive && u.attacker && u.cell != null);
  if (attackers.length) {
    const near = attackers.sort((x, y) => dist(x.cell!, core) - dist(y.cell!, core) || x.id - y.id)[0];
    return dist(near.cell!, core) <= CFG.defense.ai.chaseRadius ? near.cell! : guard;
  }
  // 準備中：次の波の直前は本陣の前へ戻る。それまでは本陣に近い野良から倒す（寝返ると本陣のそばに攻め手が湧く）
  const nextIn = b.defense!.wave < b.defense!.total ? R.defenseWaveTurn(b.defense!.wave + 1) - b.stats.playerTurns : 99;
  if (nextIn <= 1) return guard;
  let best: number | null = null;
  let bestS = Infinity;
  for (const u of b.actors) {
    if (!u.alive || u.cell == null || u.kind !== 'neutral' || u.side !== 'neutral') continue;
    // 遠い野良は寝返っても本陣まで遠いので後回し（準備は短い）
    if (dist(u.cell, core) > CFG.defense.ai.prepRadius) continue;
    const s = walkDistance(b, a, a.cell!, u.cell) + dist(u.cell, core) * 0.8;
    if (s < bestS) (bestS = s), (best = u.cell);
  }
  return best ?? guard;
}

/** 攻め手の通り道（敵陣の奥の左右から自本陣まで） */
function defenseRoutes(b: Battle, side: Side): Set<number> {
  const core = b.cores[side].cell;
  const out = new Set<number>();
  const starts = CELLS.filter((c) => c.row <= 1 && b.tiles[c.i].t === 'grass');
  const from = [starts.filter((c) => c.col < 3)[0], starts.filter((c) => c.col >= 3).slice(-1)[0], { i: b.homes[R.enemyOf(side)] }];
  for (const s of from) {
    if (!s) continue;
    const prev = new Map<number, number | null>([[s.i, null]]);
    const q = [s.i];
    for (let i = 0; i < q.length; i++) {
      const c = q[i];
      if (neighbors(c).includes(core)) {
        for (let x: number | null = c; x != null; x = prev.get(x) ?? null) out.add(x);
        break;
      }
      for (const n of neighbors(c))
        if (!prev.has(n) && b.tiles[n].t !== 'block' && b.tiles[n].t !== 'core' && b.tiles[n].t !== 'home') {
          prev.set(n, c);
          q.push(n);
        }
    }
  }
  return out;
}

/** 本陣の前の守りの位置：通り道のうち本陣から2マスの所（無ければ帰還マス） */
function defenseGuardCell(b: Battle, side: Side): number {
  const core = b.cores[side].cell;
  const cells = [...defenseRoutes(b, side)].filter((c) => dist(c, core) === 2 && !b.actors.some((u) => u.alive && u.kind === 'building' && u.cell === c));
  return cells.sort((x, y) => x - y)[Math.floor(cells.length / 2)] ?? b.homes[side];
}

function goalCell(b: Battle, a: Actor, opts: AiOptions = {}): number {
  if (b.defense) return defenseGoal(b, a);
  const side = a.side as Side;
  const foe = R.heroOf(b, R.enemyOf(side));
  const myCore = b.cores[side];
  const foeCore = b.cores[R.enemyOf(side)];
  const coreDmg = R.damageCalc(b, a, coreTarget(b, R.enemyOf(side)));
  // 砦で手番が足りなくなりそう：時間切れも負けなので、守りや寄り道より本陣へ（結界の下限で番人を倒すべきときは下へ）
  const wardStuck = CFG.wardFloor > 0 && R.wardOn(b, R.enemyOf(side)) && foeCore.hp <= Math.ceil(foeCore.max * CFG.wardFloor) * 1.05;
  if (!wardStuck && deadlineNear(b, a)) return foeCore.cell;
  if (foeThreatensCore(b, foe, side, !!opts.v3)) {
    // 先に相手の本陣を落とせるなら競走に行く（v3：手番の見積もりで比べる。旧：一撃で割れるときだけ）
    const raceWins = opts.v3 ? coreRaceTurns(b, a, R.enemyOf(side)) < coreRaceTurns(b, foe!, side) : coreDmg >= foeCore.hp && dist(a.cell!, foeCore.cell) <= 4;
    if (!raceWins) return foe!.cell!;
  }
  // 自本陣へ向かう攻城の妖：迫っていれば迎え撃つ
  const siege = b.actors
    .filter((u) => u.alive && u.cell != null && u.siegeTarget === side && walkDistance(b, u, u.cell, myCore.cell) <= W.siegeGuardDist)
    .sort((x, y) => walkDistance(b, a, a.cell!, x.cell!) - walkDistance(b, a, a.cell!, y.cell!))[0];
  if (siege && !(coreDmg >= foeCore.hp && dist(a.cell!, foeCore.cell) <= 4)) return siege.cell!;
  if (opts.policy === 'rush') return foeCore.cell;
  // 結界で本陣を割り切れない（wardFloor）：本陣が下限まで削れたら番人を倒しに行く
  if (wardStuck) {
    const guard = b.actors.find((u) => u.kind === 'boss' && u.alive && u.cell != null && u.side === R.enemyOf(side));
    if (guard && a.hp > a.hpMax * 0.35) return guard.cell!;
  }
  // オートの目標（式神候補・龍脈）が残っていれば、それを済ませてから本陣へ
  const obj = objectivesOf(b, side, opts);
  if (obj.hold) {
    // 目標を回っている間は相手の本陣攻めを許しやすい：相手が自本陣へ向かっていれば先に迎え撃つ
    if (foe?.alive && foe.cell != null && walkDistance(b, foe, foe.cell, myCore.cell) <= OBJ.guardDist && walkDistance(b, a, a.cell!, foe.cell) <= OBJ.guardDist + 2) return foe.cell;
    const g = objectiveGoal(b, a, obj);
    if (g != null) return g;
  }
  const max = a.hpMax;
  const strong = !foe || !foe.alive || power(b, a, foe) >= power(b, foe, a) * 0.85;
  if (R.overtimeLevel(b) >= 1 && a.hp > max * 0.4) return foeCore.cell;
  const grown = R.territoryTier(b, side) >= 2 || coreDmg * 3 >= foeCore.hp;
  const boss = b.actors.find((u) => u.kind === 'boss' && u.alive && u.side !== side);
  if (
    boss &&
    strong &&
    grown &&
    a.hp > max * 0.75 &&
    (!foe?.alive || dist(foe.cell!, boss.cell!) > 3 || boss.hp <= R.damageCalc(b, a, boss, true)) &&
    walkDistance(b, a, a.cell!, boss.cell!) <= 5
  )
    return boss.cell!;
  const sty = styleOf(opts);
  // 奥義の速攻：妖力が最大なら本陣へ
  if (sty.ougiRush && !R.ougiBlock(b, a)) return foeCore.cell;
  // 速攻：育ちを待たずに本陣へ
  if (sty.push != null && a.hp > max * sty.push) return foeCore.cell;
  // 狩り：妖力が最大になるまで中立を優先
  if (sty.farm && R.surgeOf(b, side).stage < (sty.farmUntil ?? R.surgeMax()) && a.hp > max * 0.4) {
    let near: number | null = null;
    let nd = Infinity;
    for (const u of b.actors) {
      if (!u.alive || u.cell == null || u.kind !== 'neutral' || u.side === side) continue;
      const d = walkDistance(b, a, a.cell!, u.cell);
      if (d < nd) (nd = d), (near = u.cell);
    }
    if (near != null) return near;
  }
  if ((strong && grown && a.hp > max * 0.5) || (foe && !foe.alive && grown)) return foeCore.cell;
  let relay: number | null = null;
  let relayD = Infinity;
  b.tiles.forEach((t, i) => {
    if (!t.relay || t.owner === side) return;
    const d = walkDistance(b, a, a.cell!, i);
    if (d < relayD) {
      relayD = d;
      relay = i;
    }
  });
  if (relay != null && relayD <= 4 && a.hp > max * 0.45) return relay;
  let best: number | null = null;
  let bestD = Infinity;
  for (const u of b.actors) {
    if (!u.alive || u.cell == null || (u.kind !== 'neutral' && u.kind !== 'boss') || u.side === side) continue;
    if (u.kind === 'boss') {
      // v3：倒しきるまでに受ける分を見て挑めるか（readyFor）。本陣の前を番人が固める盤では番人を倒さないと本陣を叩けない
      const weak = opts.v3 ? R.damageCalc(b, a, u, R.skillReady(a)) * 6 < u.hp || !readyFor(b, a, u) : R.damageCalc(b, a, u) * 4 < u.hp || a.hp < max * 0.6;
      if (weak || (foe?.alive && dist(foe.cell!, u.cell) <= 3 && u.hp > R.damageCalc(b, a, u, true))) continue;
    }
    const d = walkDistance(b, a, a.cell!, u.cell) + (u.kind === 'boss' ? -1 : 0);
    if (d < bestD) {
      bestD = d;
      best = u.cell;
    }
  }
  return best ?? foeCore.cell;
}

interface Plan {
  cell: number;
  act: 'wait' | 'potion' | 'attack' | 'cast';
  /** 術（cast）：縮地の着地点 */
  leap?: number;
  target?: TargetRef;
  skill?: boolean;
  /** 妖力の奥義 */
  ougi?: boolean;
  score: number;
}

/** cells=null なら届くマスすべて（移動前）、配列ならそのマスだけ（移動後の行動選び） */
export function bestPlan(b: Battle, a: Actor, opts: AiOptions, cells: number[] | null): Plan | null {
  const side = a.side as Side;
  const reach = cells ? null : R.reachMap(b);
  const max = a.hpMax;
  const goal = goalCell(b, a, opts);
  const rush = opts.policy === 'rush';
  const sty = styleOf(opts);
  const home = a.cell as number;
  const moved = a.moved;
  const goalDistance = walkDistance(b, a, home, goal);
  const coreThreat = R.heroOf(b, R.enemyOf(side));
  const emergency = foeThreatensCore(b, coreThreat, side, !!opts.v3);
  const ctx = buildContext(b, a);
  const obj = objectivesOf(b, side, opts);
  // 目標（式神候補・龍脈）へ向かっているときは寄り道（相手のヒーローとの小競り合いなど）より目標を優先
  // 砦で手番が足りなくなりそうなら本陣へ急ぐ（移動・本陣への攻撃を重く、中立の寄り道を軽く）
  const hurry = !b.defense && goal === b.cores[R.enemyOf(side)].cell && deadlineNear(b, a);
  const pull = hurry ? OBJ.deadlinePull : obj.hold && (obj.relays.includes(goal) || obj.shiki.some((u) => u.cell === goal)) ? OBJ.pull : 1;
  // ダメージ系の点数は攻撃力で正規化する（章が進んで数値が大きくなっても重みが同じ意味になる）
  const k = 50 / Math.max(30, a.atk);
  let best: Plan | null = null;
  const consider = (p: Plan) => {
    p.score += ((p.cell * 7 + (p.target && p.target.kind === 'actor' ? p.target.id : 0)) % 5) * 0.01;
    if (opts.novice) p.score += ((p.cell * 31 + b.turnCount * 17) % 23) - 11;
    if (!best || p.score > best.score) best = p;
  };
  const list: [number, number[]][] = cells
    ? cells.map((c) => [c, [c]])
    : [...reach!].filter(([, r]) => r.stop).map(([c]) => [c, R.paintRoute(b, a, c)]);
  const canBuildMore = (a.builtThisTurn ?? 0) < R.buildPerTurn(a);
  const v3 = !!opts.v3;
  // 倒されるかの目安（踏みとどまり・HPが半分を切ったときの加勢の回復を足す）
  const hpLeft = v3 ? deathHp(a) : a.hp;
  // 万灯（comp:relay）：龍脈を取ると回復
  const relayHeal = v3 ? Math.min(max * R.compOf(a, 'relay'), max - a.hp) * k * 0.9 : 0;
  // 3手番ごとの加勢（周りの敵へ）が次の手番の初めに出る：敵のそばで手番を終えると当たる
  const turn3 = v3 ? kaseiLeft(a, 'turn3') : null;
  const blast = turn3?.effects.blast && ((a.turns ?? 0) + 1) % 3 === 0 ? turn3.effects.blast : 0;
  const lead = v3 && R.hasFlag(a, 'fushin_lead');
  const skillE = v3 ? skillDiceMean(b, a) : 1;
  // 妖力が最大で本陣へ向かっている：中立の寄り道は値打ちが下がる（式神候補・自本陣へ向かう攻城の妖は別）
  const late = hurry || (v3 && !b.defense && goal === b.cores[R.enemyOf(side)].cell && R.surgeOf(b, side).stage >= R.surgeMax()) ? W.neutralLate : 1;
  const shooters = lead || (v3 && R.hasFlag(a, 'eq_volley')) ? R.buildingsOf(b, side).filter((u) => R.isShooter(u.type)) : [];
  const foeUnits = b.actors.filter((u) => u.alive && u.cell != null && u.side !== side && u.kind !== 'building');
  try {
    for (const [cell, path] of list) {
      let base = 0;
      // この移動で残りの龍脈をすべて塗り、式神候補も残っていなければ、そのまま本陣を落としてよい（取り返される前に）
      const clears = obj.hold && !obj.shiki.length && obj.relays.every((c) => path.includes(c) && c !== path[0]);
      for (const c of path.slice(1)) {
        const t = b.tiles[c];
        if (rush) continue;
        if (t.relay && t.owner !== side) base += (obj.hold && obj.relays.length && !emergency ? OBJ.relayTake : 32) + relayHeal;
        if (!opts.novice && R.paintable(b, c) && t.owner !== side) base += (t.owner ? W.paintEnemy : W.paint) * (sty.paint ?? 1);
      }
      base += (goalDistance - walkDistance(b, a, cell, goal)) * W.progress * (opts.aggro ?? 1) * pull;
      // 防衛戦：本陣から離れすぎない（攻め手は本陣だけを狙うので、離れると素通りされる）
      if (b.defense) base -= Math.max(0, dist(cell, b.cores[side].cell) - CFG.defense.ai.leash) * CFG.defense.ai.leashWeight;
      const tile = b.tiles[cell];
      if (tile.pickup === 'chest' && !rush) base += side === 'blue' ? 35 : 20; // 自分の宝箱は中身（銭・霊玉・装備）もある
      if (tile.pickup === 'shrine') base += a.hp < max * 0.7 ? 30 : 5;
      a.cell = cell;
      if (!cells) a.moved = path.length - 1;
      const threats: Threat[] = [];
      let rawDanger = threatAt(b, a, cell, null, v3 ? threats : undefined);
      if (v3) rawDanger = guardedDanger(a, rawDanger, threats);
      const danger = rawDanger * k * (sty.risk ?? 1);
      const risk = rawDanger >= hpLeft ? (obj.hold ? OBJ.deathRisk : 400) : danger * (1.6 - a.hp / max);
      // 返し刀・あまのじゃく返し：隣から殴られれば斬り返す（殴られそうな所ほど返せる）
      if (v3 && rawDanger < hpLeft) base += counterValue(b, a, threats) * k * W.counter;
      if (!cells && !opts.novice && !rush && canBuildMore) {
        const p = buildOptions(b, a, ctx, opts)[0];
        if (p && p.value > 0) base += p.value * 0.5;
      }
      // 待機は防御を兼ねる
      if (sty.anchor && R.buildingsOf(b, side).some((u) => adjacent(u.cell as number, cell))) base += sty.anchor;
      // 3手番ごとの加勢（落雷・燎原の火・吹雪）：次の手番の初めに周りの敵へ当たる
      if (blast) for (const u of foeUnits) if (dist(u.cell!, cell) <= CFG.flags.ougiBlastRange) base += Math.min(R.damageCalc(b, a, u, false, blast), u.hp) * k * W.blast;
      // 守りの普請（fushin_lead）：隣の自分の攻撃の建物は威力+・被ダメ-。的が射程に入っている建物のそばがよい
      if (lead) for (const u of shooters) if (adjacent(u.cell as number, cell)) base += W.lead + (foeUnits.some((x) => dist(x.cell!, u.cell as number) <= (u.range ?? 2)) ? W.leadAim : 0);
      consider({ cell, act: 'wait', score: base - danger * 0.5 + (R.hasFlag(a, 'riposte') && !a.riposte ? 8 : 0) - 8 + (sty.wait ?? 0) });
      // 回復薬を飲んで構える（回復薬は手番を終えないので、飲んだあと待機）
      if (R.itemCount(a, 'heal') > 0 && !a.itemUsed && a.hp < max * 0.5) {
        const ph = a.spec!.potionHeal;
        consider({ cell, act: 'potion', score: base - Math.max(0, rawDanger - ph) * k * 1.2 + Math.min(ph, max - a.hp) * k * W.potion });
      }
      const ready = R.skillReady(a);
      const modes: [boolean, boolean][] = [[false, false]];
      if (ready) modes.push([true, false]);
      if (!R.ougiBlock(b, a)) modes.push([true, true]);
      for (const [useSkill, ougi] of modes) {
        const sk = a.spec!.skill;
        const refs = ougi ? R.castingOugi(a, () => R.attackTargetsAt(b, a, cell, true)) : R.attackTargetsAt(b, a, cell, useSkill);
        for (const ref of refs) {
          if (rush && ref.kind === 'actor') {
            const kind = R.actorById(b, ref.id)!.kind;
            if (kind === 'neutral' || kind === 'boss') continue;
          }
          let f = R.forecastFor(b, a, ref, useSkill, ougi)!;
          let s = 0;
          // 追撃（eq_double）：通常攻撃の後にもう一度（forecast には入っていない）。追撃で倒せば反撃もない
          if (v3 && !useSkill && ref.kind === 'actor' && !f.kill && R.hasFlag(a, 'eq_double')) {
            const d2 = R.damageCalc(b, a, R.actorById(b, ref.id)!, false, CFG.flags.eqDouble);
            const kill = f.dmg + d2 >= f.hpBefore;
            f = { ...f, dmg: f.dmg + d2, hpAfter: Math.max(0, f.hpAfter - d2), kill, counter: kill ? 0 : f.counter, riposte: kill ? 0 : f.riposte };
          }
          // 術の賽（rules 2）：威力は平均で上がる（倒せるかは最低の目で見る）
          const dmg = (useSkill && skillE > 1 ? Math.min(f.hpBefore, f.dmg * skillE) : f.dmg) * k;
          if (ref.kind === 'core') s = dmg * W.core * (sty.core ?? 1) * (hurry ? OBJ.deadlineCore : 1) + (f.kill ? (obj.hold && !clears && obj.holdKill() ? -OBJ.holdKill : W.coreKill) : 0);
          else {
            const u = R.actorById(b, ref.id)!;
            const P = CFG.surge.points;
            if (u.kind === 'building') {
              s = Math.min(f.dmg, u.hp) * k * 0.7 + (f.kill ? (R.isShooter(u.type) || u.type === 'shuzen' || u.type === 'torii' ? 45 : 20) + surgeValue(b, side, P.building) : 0);
              if (rush) s *= 0.3;
            } else if (u.kind === 'hero') s = (dmg * W.hero + (f.kill ? W.heroKill + surgeValue(b, side, P.hero) : 0)) * (sty.hero ?? 1);
            else {
              s = (dmg * W.neutral + (f.kill ? (CFG.killValue[u.tier ?? 'small'] ?? 50) * W.killValue : 0)) * (sty.neutral ?? 1);
              if (u.kind === 'neutral' && !u.siegeTarget && !(obj.hold && obj.shiki.includes(u))) s *= late;
              if (u.kind === 'boss' && f.kill) s += W.bossKill;
              if (obj.hold && obj.shiki.includes(u)) s += f.kill ? OBJ.shikiKill : readyFor(b, a, u) ? dmg * W.neutral * (OBJ.shikiHit - 1) : 0;
              if (obj.hold && f.kill && obj.relays.includes(u.cell!)) s += OBJ.relayTake;
              if (f.kill) {
                s += u.kind === 'boss' ? surgeValue(b, side, 0, CFG.surge.bossStages) : surgeValue(b, side, P.neutral);
                // 相手ヒーローがそばにいれば、取られる前に取る
                const rival = R.heroOf(b, R.enemyOf(side));
                if (u.kind === 'neutral' && rival?.alive && rival.cell != null && dist(rival.cell, u.cell!) <= 4)
                  s += W.surgeDeny * surgeValue(b, R.enemyOf(side), P.neutral);
              }
              const foe = R.heroOf(b, R.enemyOf(side));
              if (u.kind === 'boss' && !f.kill && foe?.alive && foe.cell != null && dist(foe.cell, u.cell!) <= 3 && R.damageCalc(b, foe, u, true) >= f.hpAfter) s -= 120;
              // 自本陣へ向かう攻城の妖は止める
              if (u.siegeTarget === side) s += dmg * W.siegeHit + (f.kill ? W.siegeKill : 0);
              // 管狐（comp:kuda）：中立を倒すと撃破ポイントがもう1点
              if (v3 && f.kill && u.kind === 'neutral' && compLeft(a, 'kuda') > 0) s += surgeValue(b, side, P.neutral + 1) - surgeValue(b, side, P.neutral);
            }
            if (!f.kill) s -= f.counter * k * 0.3;
            if (v3 && u.kind !== 'building') {
              const w = u.kind === 'hero' ? W.hero * (sty.hero ?? 1) : W.neutral * (sty.neutral ?? 1);
              // 当てた相手に付く状態異常（火傷＝次の手番の初めに燃える、足止め）
              for (const x of f.inflict) {
                if (x.key === 'burn' && x.dmg) s += Math.min(x.dmg, f.hpAfter) * k * W.burn * w;
                else if (x.key === 'bind') s += u.kind === 'hero' ? 10 : 3;
              }
              if (f.kill) {
                // 大物喰い（comp:feast）：倒すと回復。鬼の怪力（倒すと加勢）
                const feast = R.compOf(a, 'feast');
                if (feast > 0) s += Math.min(max * feast, max - a.hp) * k * 0.9;
                if (kaseiLeft(a, 'kill')) s += W.kaseiKill;
              }
            }
            // 鳶口（eq_volley）：的を射程に入れている自分の攻撃の建物も一緒に撃つ
            if (v3 && shooters.length && u.cell != null && !f.kill && R.hasFlag(a, 'eq_volley')) {
              let v = 0;
              for (const t of shooters) if (dist(t.cell as number, u.cell) <= (t.range ?? 2)) v += Math.max(1, Math.round((t.power ?? 1) * CFG.flags.eqVolley * (SHOOTER_VALUE[t.type!] === 0.5 ? 0.5 : 1)));
              if (v > 0) s += Math.min(v, f.hpAfter) * k * W.volley + (v >= f.hpAfter ? 30 : 0);
            }
          }
          // 狐火の囁き（術を使うと加勢）：術の待ちが明ける・撃破ポイント
          if (v3 && useSkill) {
            const ks = kaseiLeft(a, 'skill');
            if (ks) s += (ks.effects.skill ? W.kaseiSkill : 0) + surgeValue(b, side, ks.effects.surge ?? 0);
          }
          if (useSkill) {
            s += Math.min(Math.round(sk.heal * max), max - a.hp) * k * 0.95;
            if (sk.guard) s += danger * 0.45;
            if (sk.delay && !f.kill) s += danger * 0.18;
            s -= 8;
          }
          if (ougi) s -= sty.ougiRush ? (ref.kind === 'core' ? -200 : W.ougiCost * 3) : W.ougiCost;
          for (const hit of f.splash) {
            const u = R.actorById(b, hit.id)!;
            s += Math.min(u.hp, hit.dmg) * k * 0.8 + (hit.dmg >= u.hp ? 35 : 0);
          }
          // 自本陣に迫る相手：目標を回っている間は勝って逃げ切れないので、なおさら止める
          if (emergency && ref.kind === 'actor' && ref.id === coreThreat!.id) s += obj.hold ? OBJ.guardHit : 120;
          let leftover = f.kill && ref.kind === 'actor' ? threatAt(b, a, cell, ref.id) * k * (1.6 - a.hp / max) : risk;
          if (obj.hold && f.kill && ref.kind === 'actor' && threatAt(b, a, cell, ref.id) >= hpLeft) leftover = OBJ.deathRisk;
          if (sty.kite) {
            const at = ref.kind === 'core' ? b.cores[ref.side].cell : R.actorById(b, ref.id)!.cell!;
            if (dist(cell, at) >= 2) s += sty.kite;
          }
          consider({ cell, act: 'attack', target: ref, skill: useSkill, ...(ougi ? { ougi: true } : {}), score: base - leftover + s });
        }
      }
      // 自分を中心に放つ術：盤を写して実際に放ち、結果で点数を付ける
      const j = R.castOf(a);
      // 支援の術（手番を終えない）は移動の後に別に放つ（decide）。ここでは行動の代わりになる術だけ
      if (j && !rush && !FREE_JUTSU.includes(j.kind)) {
        const castModes: boolean[] = [];
        if (ready) castModes.push(false);
        if (!R.ougiBlock(b, a)) castModes.push(true);
        for (const ougi of castModes) {
          const p = R.castPower(a, ougi)!;
          const lands = j.kind === 'leap' ? leapCandidates(b, a, cell, p.radius, goal) : [undefined];
          for (const to of lands) {
            const r = castScore(b, a, cell, to, ougi, k, danger, sty, obj, v3);
            if (r == null) continue;
            const after = to ?? cell;
            const move = to != null ? (walkDistance(b, a, cell, goal) - walkDistance(b, a, to, goal)) * W.progress * (opts.aggro ?? 1) * pull : 0;
            const leftover = to != null ? threatAt(b, a, to) * k * (1.6 - a.hp / max) : risk;
            const ougiCost = ougi ? (sty.ougiRush ? W.ougiCost * 3 : W.ougiCost) : 0;
            consider({ cell, act: 'cast', ...(to != null ? { leap: to } : {}), ...(ougi ? { ougi: true } : {}), score: base + move - leftover + r - ougiCost - 8 + (after === cell ? 0 : 0) });
          }
        }
      }
    }
  } finally {
    a.cell = home;
    a.moved = moved;
  }
  return best;
}

// ---------- 建築 ----------

interface BuildCtx {
  foe: Actor | undefined;
  core: { cell: number; hp: number; max: number };
  path: Set<number>;
  mine: Actor[];
}

/** 候補共通の情報：相手ヒーローから自本陣への最短路など */
function buildContext(b: Battle, a: Actor): BuildCtx {
  const side = a.side as Side;
  const foe = R.heroOf(b, R.enemyOf(side));
  const core = b.cores[side];
  const path = new Set<number>();
  if (b.defense) return { foe, core, path: defenseRoutes(b, side), mine: R.buildingsOf(b, side) };
  const from = foe?.alive && foe.cell != null ? foe.cell : null;
  if (from != null) {
    const prev = new Map<number, number | null>([[from, null]]);
    const q = [from];
    for (let i = 0; i < q.length; i++) {
      const c = q[i];
      if (neighbors(c).includes(core.cell)) {
        for (let x: number | null = c; x != null; x = prev.get(x) ?? null) path.add(x);
        break;
      }
      for (const n of neighbors(c))
        if (!prev.has(n) && b.tiles[n].t !== 'block' && b.tiles[n].t !== 'core') {
          prev.set(n, c);
          q.push(n);
        }
    }
  }
  return { foe, core, path, mine: R.buildingsOf(b, side) };
}

/** 建てる価値の見積もり（上位3件） */
function buildOptions(b: Battle, a: Actor, ctx: BuildCtx, opts: AiOptions = {}): { type: BuildingType; at: number; value: number }[] {
  if (b.phase === 'start') return [];
  const out: { type: BuildingType; at: number; value: number }[] = [];
  const side = a.side as Side;
  if (b.defense) return defenseBuildOptions(b, a, ctx, opts);
  const types = CFG.buildingOrder.filter((t) => !R.canBuildFor(b, a, t) && ctx.mine.filter((u) => u.type === t).length < TYPE_CAP[t]);
  if (!types.length) return out;
  const { foe, core, path } = ctx;
  const enemyCore = b.cores[R.enemyOf(side)];
  const neutrals = b.actors.filter((u) => u.alive && u.cell != null && u.kind === 'neutral');
  const wall = !!opts.v3 && R.hasFlag(a, 'fushin_lead');
  for (const type of types) {
    const cells = R.buildCellsFor(b, a, type);
    const st = R.buildingStats(a, type, core.max);
    const cost = R.buildCost(a, type);
    for (const at of cells) {
      let v = 0;
      if (R.isShooter(type)) {
        let hits = 0;
        if (foe?.alive && foe.cell != null && dist(foe.cell, at) <= st.range + 2) hits += 1.4;
        if (path.has(at) || dist(at, core.cell) <= 3) hits += 1.2;
        // 守りの普請（鬼の棟梁・版3）：本陣のそばの建物は本陣を守る（coreGuardMult）
        if (wall && dist(at, core.cell) <= CFG.v3.fushinCoreGuard.range) hits += W.wall;
        if (dist(at, enemyCore.cell) <= st.range + 2) hits += 1;
        if (neutrals.some((u) => dist(u.cell!, at) <= st.range)) hits += 1.5;
        v = st.power * hits * 1.8 * (50 / Math.max(30, a.atk)) * (SHOOTER_VALUE[type] ?? 1);
      } else if (type === 'sakaku') {
        if (path.has(at) && dist(at, core.cell) <= 4) v = 32;
      } else if (type === 'nobori') {
        const fight = (foe?.alive && foe.cell != null && dist(foe.cell, at) <= 4) || neutrals.some((u) => dist(u.cell!, at) <= 2);
        if (fight) v = 200 * st.power;
      } else if (type === 'otoshiana') {
        if (path.has(at) && !(foe?.cell != null && dist(foe.cell, at) <= 1)) v = st.power * 1.6 * (50 / Math.max(30, a.atk));
      } else if (type === 'shuzen') {
        const missing = core.max - core.hp;
        if (missing > core.max * 0.15) v = (Math.min(st.power * 5, missing) / core.max) * 300;
      } else if (type === 'torii') {
        const home = b.homes[side];
        const fwd = dist(home, enemyCore.cell) - dist(at, enemyCore.cell);
        if (fwd >= 3) v = 18 + 4 * fwd;
      } else if (type === 'kitsunebi') {
        let k = 0;
        for (const n of neighbors(at)) if (b.tiles[n].owner === side && path.has(n)) k = Math.max(k, 1.5);
        if (foe?.alive && foe.cell != null && dist(foe.cell, at) <= 3) k += 1;
        if (neutrals.some((u) => u.engaged && dist(u.cell!, at) <= 2)) k += 0.5;
        v = st.power * k * 1.4 * (50 / Math.max(30, a.atk));
      }
      if (v <= 0) continue;
      v *= (styleOf(opts).build ?? 1) * (styleOf(opts).buildType?.[type] ?? 1);
      out.push({ type, at, value: v - cost * 2 + 12 });
    }
  }
  return out.sort((x, y) => y.value - x.value || x.at - y.at).slice(0, 3);
}

/** 防衛戦の建築：攻め手の通り道に、柵で足を止め・櫓で撃ち・落とし穴で削る。陣材は余らせない */
function defenseBuildOptions(b: Battle, a: Actor, ctx: BuildCtx, opts: AiOptions = {}): { type: BuildingType; at: number; value: number }[] {
  const out: { type: BuildingType; at: number; value: number }[] = [];
  const side = a.side as Side;
  const { core, path, mine } = ctx;
  const P = CFG.defense.ai;
  const k = 50 / Math.max(30, a.atk);
  const count = (t: BuildingType) => mine.filter((u) => u.type === t).length;
  const cap = (t: BuildingType) => Math.ceil((P.cap[t] ?? 1) * (a.spec?.build.maxCount ?? 3) / 3);
  for (const type of CFG.buildingOrder) {
    if (R.canBuildFor(b, a, type) || count(type) >= cap(type)) continue;
    const st = R.buildingStats(a, type, core.max);
    const cost = R.buildCost(a, type);
    for (const at of R.buildCellsFor(b, a, type)) {
      const d = dist(at, core.cell);
      let v = 0;
      if (R.isShooter(type)) {
        // 射程に入る通り道のマス（本陣に近いほど重い）
        let cover = 0;
        for (const c of path) if (dist(c, at) <= st.range) cover += dist(c, core.cell) <= 3 ? 1.5 : 1;
        if (path.has(at)) cover *= 0.7; // 道の上は殴られやすい
        // 守りの普請（鬼の棟梁・版3）：本陣のそばの建物は本陣を守る（coreGuardMult）
        if (opts.v3 && R.hasFlag(a, 'fushin_lead') && d <= CFG.v3.fushinCoreGuard.range) cover += W.wall;
        v = st.power * k * cover * P.yagura * (SHOOTER_VALUE[type] ?? 1);
      } else if (type === 'sakaku') {
        // 本陣から2〜4マスの通り道をふさいで足を止める（櫓の射程の中ならなお良い）
        if (path.has(at) && d >= 2 && d <= 4) {
          const covered = mine.some((u) => R.isShooter(u.type) && dist(u.cell!, at) <= (u.range ?? 2));
          v = P.sakaku * (covered ? 1.5 : 1);
        }
      } else if (type === 'otoshiana') {
        if (path.has(at) && d >= 2 && d <= 5) v = st.power * k * P.trap;
      } else if (type === 'kitsunebi') {
        let n = 0;
        for (const c of neighbors(at)) if (path.has(c) && b.tiles[c].owner === side) n++;
        v = st.power * k * n * P.lantern;
      } else if (type === 'nobori') {
        if (d <= 3) v = 200 * st.power * 0.6;
      } else if (type === 'shuzen') {
        const missing = core.max - core.hp;
        if (missing > core.max * 0.1) v = (Math.min(st.power * 5, missing) / core.max) * 300;
      }
      if (v <= 0) continue;
      v *= (styleOf(opts).build ?? 1) * (styleOf(opts).buildType?.[type] ?? 1);
      out.push({ type, at, value: v - cost * P.costWeight + 10 });
    }
  }
  return out.sort((x, y) => y.value - x.value || x.at - y.at).slice(0, 3);
}

// ---------- 術（自分を中心に放つもの）の判断 ----------

/** 縮地の着地点の候補：隣に斬れる敵がいるマスと、目標へ一番近づくマス（数を絞って写しの回数を抑える） */
function leapCandidates(b: Battle, a: Actor, from: number, radius: number, goal: number): number[] {
  const saved = a.cell;
  a.cell = from;
  try {
    const cells = R.leapCells(b, a, radius);
    const side = a.side as Side;
    const strike = cells.filter((c) => b.actors.some((u) => u.alive && u.cell != null && u.kind !== 'building' && u.side !== side && adjacent(u.cell, c)) || adjacent(b.cores[R.enemyOf(side)].cell, c));
    const far = cells.slice().sort((x, y) => walkDistance(b, a, x, goal) - walkDistance(b, a, y, goal))[0];
    return [...new Set([...strike, ...(far != null ? [far] : [])])].slice(0, 8);
  } finally {
    a.cell = saved;
  }
}

/** 盤を写し、そのマスで術を放ったときの結果の点数（放てなければ null） */
function castScore(b: Battle, a: Actor, cell: number, to: number | undefined, ougi: boolean, k: number, danger: number, sty: AiStyle, obj: Objectives, v3 = false): number | null {
  const copy: Battle = JSON.parse(JSON.stringify(b));
  const me = copy.actors.find((u) => u.id === a.id)!;
  me.cell = cell;
  copy.phase = 'moved';
  copy.current = a.id;
  const r = R.cmdCast(copy, to, ougi);
  if (!r.ok) return null;
  const side = a.side as Side;
  const hpBefore = new Map(b.actors.map((u) => [u.id, u.hp]));
  let s = 0;
  for (const e of r.events) {
    switch (e.t) {
      case 'hit': {
        const u = b.actors.find((x) => x.id === e.id);
        if (!u || u.side === side) break;
        const dealt = Math.min(e.dmg, hpBefore.get(u.id) ?? e.dmg);
        s += dealt * k * (u.kind === 'hero' ? W.hero * (sty.hero ?? 1) : u.kind === 'building' ? 0.7 : W.neutral * (sty.neutral ?? 1));
        if (e.hp <= 0) {
          if (u.kind === 'hero') s += W.heroKill;
          else if (u.kind === 'building') s += 25;
          else {
            s += (CFG.killValue[u.tier ?? 'small'] ?? 50) * W.killValue + (u.kind === 'boss' ? W.bossKill : 0);
            s += surgeValue(b, side, u.kind === 'boss' ? 0 : CFG.surge.points.neutral, u.kind === 'boss' ? CFG.surge.bossStages : 0);
            if (obj.hold && obj.shiki.some((x) => x.id === u.id)) s += OBJ.shikiKill;
          }
        }
        break;
      }
      case 'coreHit':
        if (e.side !== side) s += e.dmg * k * W.core * (sty.core ?? 1);
        break;
      case 'coreDown':
        if (e.side !== side) s += obj.hold && obj.holdKill() ? -OBJ.holdKill : W.coreKill;
        break;
      case 'paint':
        s += e.cells.length * W.paint * (sty.paint ?? 1);
        break;
      case 'capture':
        s += 32;
        break;
      case 'build':
        s += (R.isShooter(e.type) ? 30 : 10) * (sty.build ?? 1);
        break;
      case 'heal':
        if (e.id === a.id) s += Math.min(e.amount, a.hpMax - a.hp) * k * 0.95;
        else s += e.amount * k * 0.3;
        break;
      case 'coreRepair':
        if (e.side === side) s += e.amount * k * 0.6;
        break;
      case 'status':
        if (e.id === a.id && e.key === 'ward') s += danger * 0.5;
        else if (e.key === 'burn') s += 14;
        break;
      case 'delay':
        s += 6;
        break;
      case 'push':
        s += 8 + danger * 0.15;
        break;
      case 'zone':
        // 灯籠陣：ついて回るので、続く手番ぶんの与ダメ・被ダメ・回復の見込み
        if (e.kind === 'lantern' && e.cells.length) s += 30 + danger * 0.4 + Math.min(a.hpMax - a.hp, a.hpMax * 0.12) * k * 0.6;
        // 狐火の陣：中にいる敵は手番の初めに燃える（3手番）。中の敵の数と、陣の中の自分の安全
        else if (v3 && e.kind === 'foxfire' && e.cells.length) {
          const foes = b.actors.filter((u) => u.alive && u.cell != null && u.side !== side && u.kind !== 'building' && e.cells.includes(u.cell)).length;
          s += 6 + foes * 10;
        }
        break;
      case 'surge':
        // 加勢などで入った撃破ポイント・段階
        if (v3 && e.side === side && !e.drop) s += (e.points - R.surgeOf(b, side).points) * W.surgePoint + Math.max(0, e.stage - e.prev) * W.surgeStage;
        break;
      case 'companion':
        // 狐火の囁き（術を使うと加勢）：術の待ちが明ける
        if (v3 && e.id === a.id && e.ability === 'ougi' && kaseiLeft(a, 'skill')?.effects.skill) s += W.kaseiSkill;
        break;
      case 'defend':
        // 月下の構え：受ける危険が大きいほど価値があり、隣の敵へ斬り返す見込み
        s += danger * 0.6 + b.actors.filter((u) => u.alive && u.cell != null && u.side !== side && u.kind !== 'building' && adjacent(u.cell, cell)).length * 12;
        break;
    }
  }
  return s;
}

/** そのマスで術を放つと相手の本陣が落ちるか */
function castKillsCore(b: Battle, a: Actor, cell: number, to: number | undefined, ougi: boolean): boolean {
  const copy: Battle = JSON.parse(JSON.stringify(b));
  const me = copy.actors.find((u) => u.id === a.id)!;
  me.cell = cell;
  copy.phase = 'moved';
  copy.current = a.id;
  const r = R.cmdCast(copy, to, ougi);
  return r.ok && r.events.some((e) => e.t === 'coreDown' && e.side !== a.side);
}

/** 支援の術（手番を終えない）：今いるマスで放つ価値があれば返す（奥義版は妖力を失う分を引いて比べる） */
function freeCast(b: Battle, a: Actor, opts: AiOptions): Command | null {
  const j = R.castOf(a);
  if (!j || !FREE_JUTSU.includes(j.kind) || opts.policy === 'rush' || a.cell == null) return null;
  const sty = styleOf(opts);
  const k = 50 / Math.max(30, a.atk);
  const danger = threatAt(b, a, a.cell) * k * (sty.risk ?? 1);
  const obj = objectivesOf(b, a.side as Side, opts);
  let best: { ougi: boolean; score: number } | null = null;
  let bestAt: number | undefined;
  for (const ougi of [false, true]) {
    if (ougi ? R.ougiBlock(b, a) : !R.skillReady(a)) continue;
    const p = R.castPower(a, ougi)!;
    for (const to of p.range ? aimCandidates(b, a, p.range, p.radius) : [undefined]) {
      const s = castScore(b, a, a.cell, to, ougi, k, danger, sty, obj, !!opts.v3);
      if (s == null) continue;
      const score = s - (ougi ? (sty.ougiRush ? W.ougiCost * 3 : W.ougiCost) : 0);
      if (!best || score > best.score) (best = { ougi, score }), (bestAt = to);
    }
  }
  return best && best.score > 12 ? { t: 'cast', ...(bestAt != null ? { cell: bestAt } : {}), ...(best.ougi ? { ougi: true } : {}) } : null;
}

/** 送り火の狙うマスの候補：範囲に入る敵と、まだ自陣でない塗れるマスの多い順に数個（写しの回数を抑える） */
function aimCandidates(b: Battle, a: Actor, range: number, radius: number): number[] {
  const side = a.side as Side;
  const cells = R.aimCells(b, a, range);
  const score = (c: number) => {
    const area = CELLS.filter((x) => dist(x.i, c) <= radius).map((x) => x.i);
    const foes = b.actors.filter((u) => u.alive && u.cell != null && u.kind !== 'building' && u.side !== side && area.includes(u.cell)).length;
    const paint = area.filter((x) => R.paintable(b, x) && b.tiles[x].owner !== side).length;
    return foes * 4 + paint;
  };
  return cells.map((c) => [c, score(c)] as const).sort((x, y) => y[1] - x[1] || x[0] - y[0]).slice(0, 5).map(([c]) => c);
}
